import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMain } from '../../hooks/useMain';

const norm = (s) => (s || '').toString().trim().toLowerCase();

// Column headers expected in the uploaded file (also used for the sample download)
const TEMPLATE_HEADERS = [
  'vendorName',
  'productName',
  'hsnCode',
  'volume',
  'unit',
  // 'mrp',
  // 'purchasePrice',
  'sellPrice',
  'productCount',
  'barcode',
  'rewardPoints',
  'staffIncentive',
  'incentiveType',
  'GST',
  'taxStatus',
];

const REQUIRED_FIELDS = ['productName', 'sellPrice'];

const getField = (row, key) => {
  // supports exact key or any casing variant already present in the sheet
  if (row[key] !== undefined) return row[key];
  const rowKeys = Object.keys(row);
  const match = rowKeys.find((k) => norm(k) === norm(key));
  return match !== undefined ? row[match] : undefined;
};

// A row is considered a duplicate of another if productName + vendorName + barcode all match
// (barcode alone is also treated as a strong duplicate signal when present)
const duplicateKey = (r) => `${norm(r.productName)}|${norm(r.vendorName)}|${norm(r.barcode)}`;

const BulkUploadProductDialog = ({ onUploaded }) => {
  const { activeBranchId, createManyProduct, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]); // rowIndex[]
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetState();
  };

  const downloadExcelTemplate = () => {
    const sampleData = [
      {
        vendorName: 'akash',
        productName: 'Shampoo',
        hsnCode: '33051010',
        volume: 500,
        unit: 'ml',
        // mrp: 500,
        // purchasePrice: 400,
        sellPrice: 450,
        productCount: 10,
        barcode: '123456789',
        rewardPoints: 10,
        staffIncentive: 5,
        incentiveType: '%',
        GST: 18,
        taxStatus: 'Inclusive',
      },
      {
        vendorName: 'akash',
        productName: 'Shampoo two',
        hsnCode: '33051010',
        volume: 500,
        unit: 'ml',
        // mrp: 500,
        // purchasePrice: 400,
        sellPrice: 450,
        productCount: 10,
        barcode: '123456789',
        rewardPoints: 10,
        staffIncentive: 5,
        incentiveType: 'INR',
        GST: 12,
        taxStatus: 'Exclusive',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'product_template.xlsx');
  };

  const buildDuplicateFlags = (rows) => {
    const counts = new Map();
    rows.forEach((r) => {
      if (r.invalid) return;
      const key = duplicateKey(r);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.map((r) => ({
      ...r,
      isDuplicate: !r.invalid && counts.get(duplicateKey(r)) > 1,
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!jsonData.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        let mapped = jsonData.map((item, idx) => {
          const vendorName = (getField(item, 'vendorName') || '').toString().trim();
          const productName = (getField(item, 'productName') || '').toString().trim();
          const hsnCode = (getField(item, 'hsnCode') || '').toString().trim();
          // const mrpRaw = getField(item, 'mrp');
          // const mrp = Number(mrpRaw);
          const volumeRaw = getField(item, 'volume');
          const volume = volumeRaw === '' ? '' : Number(volumeRaw);
          const unit = (getField(item, 'unit') || '').toString().trim();
          // const purchasePriceRaw = getField(item, 'purchasePrice');
          // const purchasePrice = purchasePriceRaw === '' ? '' : Number(purchasePriceRaw);
          const productCountRaw = getField(item, 'productCount');
          const productCount = productCountRaw === '' ? '' : Number(productCountRaw);
          const sellPriceRaw = getField(item, 'sellPrice');
          const sellPrice = Number(sellPriceRaw);
          const rewardPointsRaw = getField(item, 'rewardPoints');
          const rewardPoints = rewardPointsRaw === '' ? '' : Number(rewardPointsRaw);
          const staffIncentiveRaw = getField(item, 'staffIncentive');
          const staffIncentive = staffIncentiveRaw === '' ? '' : Number(staffIncentiveRaw);
          const incentiveType = (getField(item, 'incentiveType') || '%').toString().trim();
          const barcode = (getField(item, 'barcode') || '').toString().trim();
          const gstRaw = getField(item, 'GST');
          const GST = gstRaw === '' || gstRaw === undefined ? 18 : Number(gstRaw);
          const taxStatusRaw = (getField(item, 'taxStatus') || '').toString().trim();
          const taxStatus =
            taxStatusRaw === 'Exclusive' ? 'withoutTax' : taxStatusRaw === 'Inclusive' ? 'withTax' : 'noGst';

          const row = {
            rowIndex: idx + 2, // header row + 1-index
            vendorName,
            productName,
            hsnCode,
            volume,
            unit,
            // mrp: isNaN(mrp) ? '' : mrp,
            // purchasePrice: isNaN(purchasePrice) ? '' : purchasePrice,
            sellPrice: isNaN(sellPrice) ? '' : sellPrice,
            productCount: isNaN(productCount) ? '' : productCount,
            barcode,
            rewardPoints: isNaN(rewardPoints) ? '' : rewardPoints,
            staffIncentive: isNaN(staffIncentive) ? '' : staffIncentive,
            incentiveType,
            GST: isNaN(GST) ? 18 : GST,
            taxStatus,
          };

        const missingFields = [];
          if (!row.productName) missingFields.push('productName');
          // if (row.mrp === '') missingFields.push('mrp');
          if (row.sellPrice === '') missingFields.push('sellPrice');

          row.invalid = missingFields.length > 0;
          row.invalidReason = missingFields.length
            ? `Missing/invalid: ${missingFields.join(', ')}`
            : '';
          return row;
        });

        mapped = buildDuplicateFlags(mapped);
        setParsedRows(mapped);
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .xls file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRemoveRow = (rowIndex) => {
    setParsedRows((prev) => buildDuplicateFlags(prev.filter((r) => r.rowIndex !== rowIndex)));
    setSelectedRows((prev) => prev.filter((i) => i !== rowIndex));
  };

  const handleRemoveAllDuplicates = () => {
    // Keeps the first occurrence of each duplicate key, removes the rest
    const seen = new Set();
    const deduped = [];
    parsedRows.forEach((r) => {
      if (r.invalid) {
        deduped.push(r);
        return;
      }
      const key = duplicateKey(r);
      if (seen.has(key)) return; // drop this duplicate
      seen.add(key);
      deduped.push(r);
    });
    const removedCount = parsedRows.length - deduped.length;
    setParsedRows(buildDuplicateFlags(deduped));
    setSelectedRows((prev) => prev.filter((i) => deduped.some((r) => r.rowIndex === i)));
    setShowMessage?.({
      message: removedCount > 0 ? `Removed ${removedCount} duplicate row(s)` : 'No duplicates found',
      messageType: removedCount > 0 ? 'success' : 'error',
    });
  };

  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : selectableRows.map((r) => r.rowIndex));
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const duplicateCount = parsedRows.filter((r) => r.isDuplicate).length;
  const readyCount = selectableRows.length;

  const handleSubmit = async () => {
    if (parsedRows.length === 0) {
      setShowMessage({ message: 'Upload a valid file', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({ message: 'Select at least one row to upload', messageType: 'error' });
      return;
    }

    const rowsToUpload = parsedRows
      .filter((r) => selectedRows.includes(r.rowIndex))
      .map((r) => ({
        vendorName: r.vendorName,
        productName: r.productName,
        hsnCode: r.hsnCode,
        // mrp: r.mrp,
        volume: r.volume,
        unit: r.unit,
        // purchasePrice: r.purchasePrice,
        productCount: r.productCount,
        sellPrice: r.sellPrice,
        rewardPoints: r.rewardPoints,
        staffIncentive: r.staffIncentive,
        incentiveType: r.incentiveType || '%',
        barcode: r.barcode,
        GST: r.GST || 18,
        taxStatus: r.taxStatus,
      }));

    setSubmitting(true);
    try {
      const res = await createManyProduct(activeBranchId, { bulkData: rowsToUpload });
      if (res?.statusCode === 200) {
        setShowMessage({ message: res.message || 'Uploaded successfully', messageType: 'success' });
        handleClose();
        onUploaded?.();
      } else {
        setShowMessage({ message: res?.message || 'Upload failed', messageType: 'error' });
      }
    } catch (err) {
      setShowMessage({ message: err.message || 'Something went wrong', messageType: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<MdCloudUpload />}
          sx={{ background: '#6174DD', marginBottom: '0px' }}
          onClick={handleOpen}
        >
          Bulk Upload Product
        </Button>
      </Box>

              <Dialog
                open={open}
                onClose={submitting ? undefined : handleClose}
                maxWidth={parsedRows.length > 0 ? 'lg' : 'sm'}
                fullWidth
              >        <DialogTitle>Bulk Upload Products <Tooltip
              title="Upload an excel file with columns: vendorName, productName, hsnCode, volume, unit, sellPrice, productCount, barcode, rewardPoints, staffIncentive, incentiveType, GST, taxStatus. Preview and fix the data below before uploading — duplicate rows (same product + vendor + barcode) are flagged automatically and can be removed."
              placement="right"
              arrow
            ><IconButton size="small" sx={{ p: 0 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
              </Tooltip>
              </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={downloadExcelTemplate}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${readyCount} valid`} color="success" />
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" />}
                {duplicateCount > 0 && <Chip label={`${duplicateCount} duplicate rows`} color="warning" />}
                <Chip label={`${selectedRows.length} selected`} color="primary" variant="outlined" />
                {duplicateCount > 0 && (
                  <Button size="small" color="warning" variant="outlined" onClick={handleRemoveAllDuplicates}>
                    Remove duplicates (keep first)
                  </Button>
                )}
              </Box>

              <Box sx={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>HSN</TableCell>
                      <TableCell>Volume/Unit</TableCell>
                      {/* <TableCell>MRP</TableCell> */}
                      {/* <TableCell>Purchase</TableCell> */}
                      <TableCell>Sell</TableCell>
                      <TableCell>Count</TableCell>
                      <TableCell>Barcode</TableCell>
                      <TableCell>GST</TableCell>
                      <TableCell>Tax Status</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid
                            ? '#ffebee'
                            : r.isDuplicate
                            ? '#fff3e0'
                            : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.vendorName || '—'}</TableCell>
                        <TableCell>{r.productName || '—'}</TableCell>
                        <TableCell>{r.hsnCode || '—'}</TableCell>
                        <TableCell>
                          {r.volume !== '' ? r.volume : '—'} {r.unit}
                        </TableCell>
                        {/* <TableCell>{r.mrp === '' ? '—' : r.mrp}</TableCell> */}
                        {/* <TableCell>{r.purchasePrice === '' ? '—' : r.purchasePrice}</TableCell> */}
                        <TableCell>{r.sellPrice === '' ? '—' : r.sellPrice}</TableCell>
                        <TableCell>{r.productCount === '' ? '—' : r.productCount}</TableCell>
                        <TableCell>{r.barcode || '—'}</TableCell>
                        <TableCell>{r.GST}</TableCell>
                        <TableCell>{r.taxStatus}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Tooltip title={r.invalidReason || 'Invalid row'}>
                              <Chip size="small" label="Invalid" color="error" />
                            </Tooltip>
                          ) : r.isDuplicate ? (
                            <Chip size="small" label="Duplicate" color="warning" />
                          ) : (
                            <Chip size="small" label="Ready" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color={r.invalid ? 'error' : 'text.secondary'}>
                            {r.invalid ? r.invalidReason : r.isDuplicate ? 'Same product+vendor+barcode as another row' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Remove row">
                            <IconButton size="small" onClick={() => handleRemoveRow(r.rowIndex)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Selected Product(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadProductDialog;